import { useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@components/ui';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import { getFormTemplates } from '@/services/formTemplateService';
import { useAuthStore } from '@/stores/authStore';
import { useCountries } from '@/hooks/useCountries';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { FormTemplateSummary } from '@/types/formTemplate';
import type { FilterFieldConfig } from '@/types/filters';
import PlusIcon from '@assets/icons/PlusIcon';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import FormsEmptyIcon from '@assets/icons/FormsEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState } from '@utils/tabHelpers';
import type { TableSearchParams } from '@utils/tabHelpers';
import { useTranslation } from '@/hooks/useTranslation';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { useAuditTrigger } from '@/hooks/useAuditTrigger';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/entity-templates/')({
  validateSearch: (search: Record<string, unknown>) =>
    validateTableSearch(search, { sortBy: 'created', sortDir: 'desc' }),
  component: EntityTemplateListPage,
});

function EntityTemplateListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const audit = useAuditTrigger();
  const auditOpen = audit.open;
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  const { countries } = useCountries();

  const countryMap = useMemo(
    () => new Map(countries.map((c) => [c.id, c.name])),
    [countries],
  );

  const countryOptions = useMemo(
    () => countries.map((c) => ({ label: c.name, value: c.id })),
    [countries],
  );

  // --- Filters (ordered to match column order: name, country, ..., created, updated) ---
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'name',
        label: t('K_COLUMN_TEMPLATE_NAME', 'Template Name'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_TEMPLATE_NAME', 'Search template name...'),
      },
      {
        fieldKey: 'countryId',
        label: t('K_FILTER_COUNTRY', 'Country'),
        type: 'select',
        options: countryOptions,
      },
      {
        fieldKey: 'createdAt',
        label: t('K_FILTER_CREATED', 'Created Date'),
        type: 'date-range',
        fromLabel: t('K_LABEL_FROM', 'From'),
        toLabel: t('K_LABEL_TO', 'To'),
      },
      {
        fieldKey: 'updatedAt',
        label: t('K_FILTER_UPDATED', 'Updated Date'),
        type: 'date-range',
        fromLabel: t('K_LABEL_FROM', 'From'),
        toLabel: t('K_LABEL_TO', 'To'),
      },
    ],
    [t, countryOptions],
  );

  const {
    filters,
    filterPayload,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
  } = useTableFilters({
    routeTo: '/w/entity-templates',
    filtersParam,
    fields: filterFields,
  });

  const columns = useMemo<DataTableColumn<FormTemplateSummary>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_TEMPLATE_NAME', 'Template Name'),
        sortable: true,
        sortKey: 'name',
        cell: (item) => (
          <span className={styles.cellPrimary}>{item.name || '-'}</span>
        ),
      },
      {
        id: 'country',
        header: t('K_COLUMN_COUNTRY', 'Country'),
        sortable: true,
        sortKey: 'countryId',
        cell: (item) => (
          <span className={styles.cellPrimary}>
            {(item.countryId && countryMap.get(item.countryId)) || '-'}
          </span>
        ),
      },
      {
        id: 'tabs',
        header: t('K_COLUMN_TABS', 'Tabs'),
        cell: (item) => item.tabCount,
      },
      {
        id: 'forms',
        header: t('K_COLUMN_FORMS', 'Forms'),
        cell: (item) => item.formCount,
      },
      {
        id: 'created',
        header: t('K_COLUMN_CREATED', 'Created'),
        sortable: true,
        sortKey: 'createdAt',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.createdAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'updated',
        header: t('K_COLUMN_UPDATED', 'Updated'),
        sortable: true,
        sortKey: 'updatedAt',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [t, countryMap],
  );

  const rowActions = useCallback((item: FormTemplateSummary) => {
    const actions: ProfileAction[] = [];
    if (hasPermission('ENTITY_TEMPLATES_UPDATE')) {
      actions.push({
        id: 'edit',
        label: t('K_EDIT', 'Edit'),
        onClick: () => void navigate({ to: '/w/entity-templates/add', search: { id: item.id } }),
      });
    }
    if (hasPermission('AUDIT_READ')) {
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () => auditOpen(item.id, item.name || undefined),
      });
    }
    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, navigate, hasPermission, auditOpen],
  );

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['formTemplates', 'LEGAL_ENTITY', { page, size, sort: sortString, filterPayload }],
    queryFn: () => getFormTemplates({ page, size, sort: sortString, scope: 'LEGAL_ENTITY' }, filterPayload),
  });

  const templates = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/entity-templates',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/entity-templates',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/entity-templates',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const getRowKey = useCallback((item: FormTemplateSummary) => item.id, []);

  const handleRowClick = useCallback(
    (item: FormTemplateSummary) => {
      void navigate({ to: '/w/entity-templates/view/$templateId', params: { templateId: item.id } });
    },
    [navigate],
  );

  const handleCreate = useCallback(() => {
    void navigate({ to: '/w/entity-templates/add' });
  }, [navigate]);

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <FormsEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>
            {t('K_EMPTY_NO_ENTITY_TEMPLATES', 'No entity templates yet')}
          </h2>
          <p className={styles.emptyText}>
            {t(
              'K_EMPTY_CREATE_FIRST_ENTITY_TEMPLATE',
              'Get started by creating your first entity template.',
            )}
          </p>
          {hasPermission('ENTITY_TEMPLATES_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
              {t('K_BUTTON_CREATE_TEMPLATE', 'Create Template')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>
            {t('K_PAGE_TITLE_ENTITY_TEMPLATES', 'Legal Entity Templates')}
          </h1>
          <p className={styles.subtitle}>
            {t(
              'K_PAGE_SUBTITLE_ENTITY_TEMPLATES',
              'Manage form templates for legal entities by country',
            )}
          </p>
        </div>

        {hasPermission('ENTITY_TEMPLATES_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
            {t('K_BUTTON_CREATE_TEMPLATE', 'Create Template')}
          </Button>
        )}
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={templates}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        loadingText={t('K_LOADING_ENTITY_TEMPLATES', 'Loading entity templates...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Entity templates list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <AuditTrailModal
        open={audit.target !== null}
        onClose={audit.close}
        filter={{ resourceType: AUDIT_RESOURCE.LEGAL_ENTITY_TEMPLATE, resourceId: audit.target?.id ?? '' }}
        subject={audit.target?.name}
      />
    </div>
  );
}
